import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface SolarFlareCMEProps {
  width?: number | string;
  height?: number | string;
}

const FlareFallbackCanvas: React.FC<{ size: number }> = ({ size }) => {
  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    
    // Sun
    ctx.save();
    const sunPulse = Math.sin(frameCount * 0.05) * 3;
    const gradient = ctx.createRadialGradient(centerX, centerY, 10, centerX, centerY, 60 + sunPulse);
    gradient.addColorStop(0, '#FFFFFF');
    gradient.addColorStop(0.2, '#FDE047');
    gradient.addColorStop(0.6, '#EA580C');
    gradient.addColorStop(1, 'rgba(234, 88, 12, 0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 60 + sunPulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Solar Flare / CME loop
    const time = (frameCount % 120) / 120; // 0 to 1 loop
    if (time > 0.1 && time < 0.9) {
      const flareRadius = 60 + time * 100;
      const flareAlpha = Math.max(0, 1 - (time - 0.1) * 1.5);
      
      ctx.beginPath();
      ctx.strokeStyle = `rgba(253, 224, 71, ${flareAlpha})`;
      ctx.lineWidth = 4;
      // Draw arc for CME
      ctx.arc(centerX, centerY, flareRadius, Math.PI * 1.2, Math.PI * 1.8);
      ctx.stroke();
    }
  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return <canvas ref={canvasRef} style={{ borderRadius: '12px', width: '100%', height: '100%' }} />;
};

export const SolarFlareCME: React.FC<SolarFlareCMEProps> = ({ width = '100%', height = 400 }) => {
  const [hasError, setHasError] = useState(false);
  const [showIframe, setShowIframe] = useState(true);

  // We use a YouTube iframe. If it fails, there's no native onerror for iframe contents due to CORS,
  // but we can provide a fallback if needed manually. For now we assume iframe works unless network error.
  
  return (
    <div style={{ width, height, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0F172A', borderRadius: '12px', overflow: 'hidden' }}>
      {showIframe && !hasError ? (
        <iframe
          width="100%"
          height="100%"
          src="https://www.youtube.com/embed/HqFVxWfVtoo?autoplay=1&mute=1&loop=1&playlist=HqFVxWfVtoo&controls=0&modestbranding=1"
          title="Solar Flare CME"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={() => console.log('Iframe loaded')}
          onError={() => setHasError(true)}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        />
      ) : (
        <FlareFallbackCanvas size={Number(height)} />
      )}
      
      {/* Fallback button if youtube is blocked */}
      {!hasError && showIframe && (
        <div style={{ position: 'absolute', bottom: 10, right: 10, zIndex: 10 }}>
          <button 
            onClick={() => setShowIframe(false)}
            style={{ background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', cursor: 'pointer' }}>
            Switch to 2D Fallback
          </button>
        </div>
      )}
    </div>
  );
};
